(ns app.front.view.application.work-type-target
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.application.phase-subs :as app-phase-subs]
            [app.front.view.common.data-grid :as dg]
            [app.front.view.navigation :as navigation]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.tree :as entity-tree]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::available-work-types
  :<- [::ws-subs/category-group-config]
  :<- [:language]
  (fn [[category-group-config language] _]
    (->> category-group-config
         :entity/_parent
         (config-model/sort-group-configs language))))

(rf/reg-sub ::work-targets
  :<- [::work-type]
  :<- [:language]
  (fn [[work-type-group-config language] _]
    (->> work-type-group-config
         :entity/_parent
         (filter du/is-active?)
         (config-model/sort-group-configs language))))

(rf/reg-sub ::work-type
  :<- [::work-types]
  :-> first)

(defn resolve-work-types [application workspace]
  (or (:application/work-type-group-configs application)
      (:workspace/work-type-group-configs workspace)))

(rf/reg-sub ::work-types
  :<- [::ws-subs/application]
  :<- [::ws-subs/workspace]
  :<- [:language]
  (fn [[application workspace language] _]
    (->> (resolve-work-types application workspace)
         (config-model/sort-group-configs language))))

(defn resolve-work-target [application workspace]
  (or (:application/work-target-group-config application)
      (:workspace/work-target-group-config workspace)))

(rf/reg-sub ::work-target
  :<- [::ws-subs/application]
  :<- [::ws-subs/workspace]
  (fn [[application workspace] _]
    (resolve-work-target application workspace)))

(rf/reg-sub ::forms-reloading :-> ::forms-reloading)

(rf/reg-sub ::work-type-target-set?
  :<- [::work-type]
  :<- [::work-target]
  :<- [::available-work-types]
  :<- [::work-targets]
  (fn [[work-type work-target work-types work-targets]]
    (cond
      (empty? work-types)
      true ; Category has no work types or targets; consider them "set" to nil
      (empty? work-targets)
      (some? work-type)
      :else
      (and (some? work-type)
           (some? work-target)))))

;;
;; New category / work type / target selection widget
;;

(defn- make-editor-config [workspace application]
  {:category              (:workspace/category-group-config workspace)
   :additional-categories (:workspace/additional-categories workspace)
   :work-types            (set (:application/work-type-group-configs application))
   :work-target           (:application/work-target-group-config application)})

(defn- make-save-args [{:keys [category additional-categories work-types work-target]}]
  (p/assoc-if {:category-id (:db/id category)}
              :additional-categories (->> additional-categories
                                          (map :db/id)
                                          seq
                                          set)
              :work-type-ids (some->> work-types
                                      (map :db/id)
                                      seq
                                      set)
              :work-target-id (:db/id work-target)))

(rf/reg-event-fx ::enter-edit-mode
  (fn [{:keys [db]} [_ {workspace :entity/parent}]]
    {:db           (-> (assoc-in db [:view/workspace :editing-config-path?] true)
                       (assoc ::config-in-editor (make-editor-config workspace
                                                                     (workspace-model/workspace-application workspace)))
                       (assoc ::loading? true))
     :comm/command {:command          :workspace/available-configs
                    :method           :get
                    :data             {:workspace-id (:workspace/id workspace)}
                    :on-success       [::entities/merge-entity-db]
                    :finally-dispatch [::loading-done]}
     :dispatch     [::navigation/scroll-to-top]}))

(rf/reg-event-db ::loading-done
  (fn [db _]
    (dissoc db ::loading?)))

(rf/reg-event-db ::cancel-edit-mode
  (fn [db _]
    (-> (assoc-in db [:view/workspace :editing-config-path?] false)
        (dissoc ::config-in-editor)
        (dissoc ::checked-save-opts))))

(rf/reg-event-db ::set-config-in-editor
  (fn [db [_ new-config]]
    (assoc db ::config-in-editor new-config)))

(rf/reg-event-db ::set-save-opts
  (fn [db [_ add? new-opt]]
    (update db ::checked-save-opts (comp set (if add? conj disj)) new-opt)))

(rf/reg-event-db ::clear-save-opts
  (fn [db _]
    (dissoc db ::checked-save-opts)))

(rf/reg-event-fx ::on-save-success
  (fn [_ [_ workspace new-process-flow signal]]
    (let [old-process-flow (config-model/resolve-value :group.config/process-flow workspace)]
      {:fx (concat [[:dispatch [::cancel-edit-mode]]]
                   (when (not= old-process-flow new-process-flow)
                     [[:dispatch [::process/fetch-process-states
                                  (-> workspace :workspace/municipality :municipality/country)
                                  (:workspace/permit-type workspace)
                                  new-process-flow]]])
                   (when (= signal :process.signal/set-ws-config-path-start-pre-con)
                     [[:dispatch [::app-phase-subs/set-phase-in-view :view/workspace-pre-consultation]]]))})))

(rf/reg-sub ::loading? :-> ::loading?)

(rf/reg-sub ::must-set-work-type-target?
  :<- [::ws-subs/application]
  (fn [application _]
    (when application
      (contains? (<sub [::process/signal-errors application [:process.signal/submit]])
                 :process.guard/work-type+target-set-or-not-available?))))

(rf/reg-sub ::editing?
  :<- [::ws-subs/view-data]
  :<- [::must-set-work-type-target?]
  (fn [[view-data must-set-wtt?]]
    (or (:editing-config-path? view-data)
        must-set-wtt?)))

(rf/reg-sub ::config-in-editor :-> ::config-in-editor)

(rf/reg-sub ::save-args
  :<- [::config-in-editor]
  :-> make-save-args)

(rf/reg-sub ::current-ws-config
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/application]
  (fn [[workspace application] _]
    (make-editor-config workspace application)))

(rf/reg-sub ::displayed-config
  :<- [::config-in-editor]
  :<- [::current-ws-config]
  (fn [[config-in-editor current-ws-config]]
    (or config-in-editor
        current-ws-config)))

(rf/reg-sub ::config-changed?
  :<- [::current-ws-config]
  :<- [::save-args]
  (fn [[current-ws-config save-args] _]
    (not= (make-save-args current-ws-config)
          save-args)))

(defn selected-process-flow [{:keys [category work-types work-target]} workspace]
  (->> [work-target
        (first work-types)
        category
        (:workspace/permit-type-group-config workspace)]
       (keep :group.config/process-flow)
       first))

(rf/reg-sub ::selected-process-flow
  :<- [::config-in-editor]
  :<- [::ws-subs/workspace]
  (fn [[config-in-editor workspace] _]
    (selected-process-flow config-in-editor workspace)))

(def process-change-to-draft-signals
  #{:process.signal/set-ws-config-path-restore-to-draft
    :process.signal/set-ws-config-path-start-pre-con
    :process.signal/set-ws-config-path-restore-to-draft-invalidate-pre-con})

(defn incompatible-process-change?
  [enabled? current-process-flow selected-process-flow process-state save-signal]
  (and enabled?
       selected-process-flow
       (not= current-process-flow selected-process-flow)
       (not= :process.state/draft process-state)
       (not (contains? process-change-to-draft-signals save-signal))))

(rf/reg-sub ::incompatible-process-change?
  :<- [::ff/enabled? :feature-flag.id/prevent-incompatible-process-change]
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/workspace-target]
  :<- [::selected-process-flow]
  :<- [::save-signal]
  (fn [[enabled? workspace target selected-flow save-signal] _]
    (incompatible-process-change? enabled?
                                  (config-model/resolve-value :group.config/process-flow workspace)
                                  selected-flow
                                  (:process/state target)
                                  save-signal)))

(rf/reg-sub ::new-case-id-required?
  :<- [::config-in-editor]
  :<- [::ws-subs/workspace]
  (fn [[{:keys [category work-types work-target]} workspace]]
    (config-model/new-case-id-required? workspace
                                        (workspace-model/sanitize-pt-path
                                          (:workspace/permit-type-group-config workspace)
                                          category
                                          (first work-types)
                                          work-target))))

(rf/reg-sub ::can-change-category?
  :<- [::ws-subs/own-or-prior-pre-consultation]
  (fn [pre-consultation _]
    (or (not pre-consultation)
        (du/in-state? :pre-consultation.state/not-applicable pre-consultation)
        (-> pre-consultation :granted-permissions :permission.application/review))))

(defn- compatible-config?
  "If the current workspace does NOT require a location, only show configs that also do not require it."
  [workspace new-config]
  (or (not (config-model/resolve-boolean :group.config/no-workspace-location workspace))
      (:group.config/no-workspace-location new-config)
      (->> (concat (entity-tree/lazy-ancestors new-config)
                   (eu/descendants new-config))
           (some :group.config/no-workspace-location))))

(rf/reg-sub ::category-options
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/user-has-permission? :permission.application/authority-submit]
  :<- [:language]
  (fn [[workspace authority-user? lang] _]
    (->> workspace
         :workspace/permit-type-group-config
         config-model/active-child-configs
         (filter (every-pred (partial compatible-config? workspace)
                             (partial config-model/allow-workspace-creation? authority-user? true)))
         (config-model/sort-group-configs lang))))


(rf/reg-sub ::additional-case-categories-options
  :<- [::config-in-editor]
  :<- [::category-options]
  (fn [[{:keys [category]} category-options]]
    (filter #(not= (:db/id category) (:db/id %)) category-options)))

(rf/reg-sub ::work-type-options
  :<- [::displayed-config]
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/application]
  :<- [::ws-subs/user-has-permission? :permission.application/authority-submit]
  :<- [:language]
  (fn [[{:keys [category]} workspace application authority-user? lang] _]
    (when-let [parent-id (and application
                              (:db/id category))]
      ;; Find the children with a new sub, because the category entity can be stale at this point
      (->> (<sub [::entities/entities {:entity/parent parent-id}])
           (filter (every-pred (partial compatible-config? workspace)
                               (partial config-model/allow-workspace-creation? authority-user? true)))
           (config-model/sort-group-configs lang)))))

(rf/reg-sub ::work-target-options
  :<- [::displayed-config]
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/user-has-permission? :permission.application/authority-submit]
  :<- [:language]
  (fn [[{:keys [work-types]} workspace authority-user? lang] _]
    (when-let [type-id (some-> work-types
                               first
                               :db/id)]
      ;; Find the children with a new sub, because the work-type entity can be stale at this point
      (some->> (<sub [::entities/entities {:entity/parent type-id
                                           :domain/state  :group-config.state/enabled}])
               (filter (every-pred (partial compatible-config? workspace)
                                   (partial config-model/allow-workspace-creation? authority-user? true)))
               (config-model/sort-group-configs lang)))))

(rf/reg-sub ::save-disabled?
  :<- [::config-in-editor]
  :<- [::config-changed?]
  :<- [::work-type-options]
  :<- [::work-target-options]
  :<- [::incompatible-process-change?]
  (fn [[{:keys [category work-types work-target]}
        config-changed?
        work-type-options
        work-target-options
        incompatible-process-change?] _]
    (or (not config-changed?)
        (nil? category)
        (and (empty? work-types) (seq work-type-options))
        (and (nil? work-target) (seq work-target-options))
        incompatible-process-change?)))

(def save-opt-checkboxes
  [:cat-wtt-selection/to-draft?
   :cat-wtt-selection/start-pre-con?
   :cat-wtt-selection/invalidate-pre-con?])

(def checkbox-signal-matrix
  {#{:cat-wtt-selection/to-draft?}           :process.signal/set-ws-config-path-restore-to-draft
   #{:cat-wtt-selection/start-pre-con?}      :process.signal/set-ws-config-path-start-pre-con
   #{:cat-wtt-selection/invalidate-pre-con?} :process.signal/set-ws-config-path-invalidate-pre-con
   #{:cat-wtt-selection/to-draft?
     :cat-wtt-selection/invalidate-pre-con?} :process.signal/set-ws-config-path-restore-to-draft-invalidate-pre-con})

(rf/reg-sub ::available-save-opts
  :<- [::ws-subs/workspace-target]
  :<- [::save-args]
  (fn [[target save-args] _]
    (-> (->> checkbox-signal-matrix
             (p/filter-vals (fn [signal]
                              (<sub [::process/signal-permitted?
                                     target
                                     [signal save-args]])))
             (map first)
             (apply set/union))
        (filter save-opt-checkboxes))))

(rf/reg-sub ::checked-save-opts :-> ::checked-save-opts)

(rf/reg-sub ::save-signal
  :<- [::checked-save-opts]
  (fn [checked-opts _]
    (or (get checkbox-signal-matrix checked-opts)
        :process.signal/set-ws-config-path)))


(rf/reg-sub ::read-only-work-type?
  :<- [::work-types]
  :<- [::ws-subs/user-has-permission? :permission.application/authority-submit]
  (fn [[work-types is-authority?] _]
    ;; applicant cannot change work-type if it is alreade selected from a restricted set, e.g. authority-only
    ;; work-type
    (and (seq work-types)
         (not is-authority?)
         (->> work-types
              (some (complement (partial config-model/allow-workspace-creation? false true)))))))

(rf/reg-sub ::read-only-work-target?
  :<- [::work-target]
  :<- [::ws-subs/user-has-permission? :permission.application/authority-submit]
  (fn [[work-target is-authority?] _]
    (and work-target
         (not is-authority?)
         (not (config-model/allow-workspace-creation? false true work-target)))))

(def selection-widget-subsection
  :div.flex.flex-wrap.border-t.border-solid.border-ui-gray.mt-2.pt-4.gap-4)

(defn read-only-configs [{:keys [configs config lang]}]
  [:div.mt-5
   (->> (or configs [config])
        (map (fn [config]
               (config-model/localized-name config lang)))
        (str/join ", "))])

(defn category-work-type-target-selection [{:keys [tr lang workspace]}]
  (let [{:keys [category
                work-types
                work-target
                additional-categories]} (<sub [::displayed-config])
        category-options                (<sub [::category-options])
        additional-categories-options   (<sub [::additional-case-categories-options])
        work-type-options               (<sub [::work-type-options])
        work-target-options             (<sub [::work-target-options])
        saving?                         (<sub [::process/signal-processing?
                                               :process.signal/set-ws-config-path
                                               :process.signal/set-ws-config-path-restore-to-draft
                                               :process.signal/set-ws-config-path-start-pre-con
                                               :process.signal/set-ws-config-path-invalidate-pre-con
                                               :process.signal/set-ws-config-path-restore-to-draft-invalidate-pre-con])
        loading?                        (boolean (or (<sub [::loading?])
                                                     saving?))
        save-args                       (<sub [::save-args])
        cat-disabled?                   (not (<sub [::can-change-category?]))
        save-opts                       (<sub [::available-save-opts])
        checked-opts                    (set (<sub [::checked-save-opts]))
        save-signal                     (<sub [::save-signal])
        incompatible-process-change?    (<sub [::incompatible-process-change?])
        phase                           (workspace-model/workspace-target workspace)
        save-permitted?                 (<sub [::process/signal-permitted?
                                               phase
                                               [save-signal save-args]])
        save-disabled?                  (boolean (or loading?
                                                     (<sub [::save-disabled?])
                                                     (not save-permitted?)))
        new-case-id-reqd?               (<sub [::new-case-id-required?])
        save!                           (fn []
                                          (>evt [::process/signal
                                                 phase
                                                 [save-signal save-args]
                                                 {:notify     {:success :changes-saved}
                                                  :on-success [::on-save-success
                                                               workspace
                                                               (selected-process-flow
                                                                 {:category    category
                                                                  :work-types  work-types
                                                                  :work-target work-target}
                                                                 workspace)
                                                               save-signal]}]))]
    [:div#ws-config-path-editor.flex.flex-col.gap-2.my-4.rounded.bg-blue-100.p-4.border-2.border-solid.border-blue-600
     [:div.flex.flex-wrap.gap-2
      [dg/grid-item {:label (tr [:wizard/category])}
       (if (and (-> workspace
                    :workspace/permit-type
                    (= :permit-type/CE))
                (ff/enabled? :feature-flag.id/allow-multiple-categories-to-a-case))
         [ui-ms/multi-select
          {:id          "category"
           :label-text  (tr [:complaint/category])
           :hide-label? true
           :value       additional-categories
           :values      additional-categories-options
           :disabled    (or loading? cat-disabled?)
           :class-name  "w-full min-w-64"
           :wrap-text?  true
           :val->str    #(config-model/localized-name % lang)
           :on-change   #(do (>evt [::set-config-in-editor {:category              category
                                                            :additional-categories %}])
                             (>evt [::clear-save-opts]))}]
         [ui-select/select {:id           "category"
                            :allow-empty? false
                            :value        category
                            :values       category-options
                            :val->str     #(config-model/localized-name % lang)
                            :class-name   "full-width-select"
                            :disabled     (or loading? cat-disabled?)
                            :on-change    #(do (>evt [::set-config-in-editor {:category %}])
                                               (>evt [::clear-save-opts]))}])]
      (when (seq work-type-options)
        [dg/grid-item {:label (tr [(if (next work-types)
                                     :work-types
                                     :work-type-select)])}
         (if (<sub [::read-only-work-type?])
           [read-only-configs (util/mmap {:configs work-types} lang)]
           (if (contains? ptc-model/permit-types-with-multiple-work-types (:workspace/permit-type workspace))
             [ui-config-pills/config-pills
              (util/mmap {:val->str        #(config-model/localized-name % lang)
                          :loading?        loading?
                          :values          work-type-options
                          :add-item        #(>evt [::set-config-in-editor {:category   category
                                                                           :work-types (set (conj work-types %))}])
                          :remove-item     #(>evt [::set-config-in-editor {:category   category
                                                                           :work-types (set (disj work-types %))}])
                          :selected        work-types
                          :add-button-text (tr [:application/add-work-type])}
                         tr)]
             [ui-select/select {:id           "work-type"
                                :allow-empty? false
                                :value        (first work-types)
                                :values       work-type-options
                                :val->str     #(config-model/localized-name % lang)
                                :class-name   "full-width-select"
                                :disabled     loading?
                                :on-change    #(>evt [::set-config-in-editor {:category   category
                                                                              :work-types #{%}}])}]))])
      (when (seq work-target-options)
        [dg/grid-item {:label (tr [:work-target-select])}
         (if (<sub [::read-only-work-target?])
           [read-only-configs (util/mmap {:config work-target} lang)]
           [ui-select/select {:id           "work-target"
                              :allow-empty? false
                              :value        work-target
                              :values       work-target-options
                              :val->str     #(config-model/localized-name % lang)
                              :class-name   "full-width-select"
                              :disabled     loading?
                              :on-change    #(>evt [::set-config-in-editor {:category    category
                                                                            :work-types  work-types
                                                                            :work-target %}])}])])]
     (when (<sub [::must-set-work-type-target?])
       [selection-widget-subsection
        [:div {:dangerouslySetInnerHTML (r/unsafe-html (if cat-disabled?
                                                         (tr [:cat-wtt-selection/must-select-guide-no-cat]
                                                             [(tr [:application/work-type])
                                                              (tr [:application/work-target])])
                                                         (tr [:cat-wtt-selection/must-select-guide]
                                                             [(tr [:workspace/category])
                                                              (tr [:categories])
                                                              (tr [:application/work-type])
                                                              (tr [:application/work-target])])))
               :style                   {:max-width "900px"}}]])
     (when (<sub [::process/signal-permitted?
                  phase
                  [:process.signal/set-ws-config-path-invalidate-pre-con save-args]])
       [selection-widget-subsection
        [:div {:style {:max-width "900px"}}
         (tr [:cat-wtt-selection/pre-con-not-applicable-guide])]])
     (when (seq save-opts)
       [selection-widget-subsection
        (for-frag [k save-opts]
          [:div
           (cc/checkbox {:id         (name k)
                         :label-text (tr [k])
                         :checked    (contains? checked-opts k)
                         :on-change  #(>evt [::set-save-opts % k])})])])
     (when incompatible-process-change?
       [:p.mt-2.text-support-error.font-semibold
        {:role "alert"}
        (tr [:cat-wtt-selection/incompatible-process-change])])
     [selection-widget-subsection
      [:div.flex.flex-row.flex-wrap.items-baseline.justify-start.gap-2
       (cc/button {:kind     :tertiary
                   :size     :small
                   :on-click #(>evt [::cancel-edit-mode])
                   :id       :cancel-ws-config-edit
                   :disabled saving?}
                  (tr [:cancel]))
       (if new-case-id-reqd?
         [ui/modal-confirmation-button
          {:id                  "save-ws-config-edit-with-confirm"
           :tr                  tr
           :modal-title         (tr [:are-you-sure])
           :trigger-button-opts {:kind     :primary
                                 :size     :small
                                 :disabled save-disabled?}
           :trigger-button-text (tr [:save])
           :on-yes-click        save!}
          (tr [:cat-wtt-selection/new-case-id-warning])]
         (cc/button {:kind     :primary
                     :size     :small
                     :on-click save!
                     :id       :save-ws-config-edit
                     :disabled save-disabled?}
                    (tr [:save])))]
      (when loading?
        [:div
         (cc/inline-loading {:description (tr [(if saving?
                                                 :saving
                                                 :loading-options)])})])]]))

(defn category-work-type-target-widget
  "Selection element for workspace header, 2023 vintage"
  [{:keys [tr workspace]}]
  (let [lang                  (<sub [:language])
        category              (<sub [::ws-subs/category-group-config])
        additional-categories (<sub [::ws-subs/additional-categories])
        work-types            (<sub [::work-types])
        work-target           (<sub [::work-target])
        editing?              (<sub [::editing?])]
    (when category
      [:div#ws-config-path-display.flex
       {:class (when-not editing? "gap-2 my-4 flex-wrap")}
       (if editing?
         [category-work-type-target-selection (util/mmap tr lang workspace)]
         [:<>
          [dg/grid-item {:label (tr [:wizard/category])}
           (config-model/localized-name category lang)]
          (when (and (ff/enabled? :feature-flag.id/allow-multiple-categories-to-a-case)
                     additional-categories)
            [dg/grid-item {:label (tr [:code-enforcement/additional-categories])
                           :class "max-w-xs"}
             [:div {:class "leading-normal"}
              (-> additional-categories
                  (config-model/localized-names lang))]])
          (when (seq work-types)
            [dg/grid-item {:label (tr [(if (next work-types)
                                         :work-types
                                         :work-type-select)])}
             (-> work-types
                 (config-model/localized-names lang))])
          (when work-target
            [dg/grid-item {:label (tr [:work-target-select])}
             (config-model/localized-name work-target lang)])])])))
